/**
 * Shipping Service
 * 
 * Shipping service for handling shipping operations and API communication including:
 * - Shipping statistics
 * - Shipment management
 * - Shipment tracking
 * - Rate shopping
 * - Carrier performance tracking
 * 
 * All methods use the centralized API client with JWT authentication, retry logic,
 * and offline queue support.
 * 
 * @module services/shippingService
 */

import { api } from './api.js';
import { API_ENDPOINTS } from '../config/api.config.js';

export const shippingService = {

  async getStats() {
    const res = await api.get(`${API_ENDPOINTS.SHIPPING}/stats`);
    return res?.data;
  },

  async getShipments(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const url = `${API_ENDPOINTS.SHIPPING}/shipments${queryParams ? `?${queryParams}` : ''}`;
    const res = await api.get(url);
    return res?.data;
  },

  async trackShipment(trackingNumber) {
    const res = await api.get(`${API_ENDPOINTS.SHIPPING}/tracking/${trackingNumber}`);
    return res?.data;
  },

  async createShipment(shipmentData) {
    const res = await api.post(`${API_ENDPOINTS.SHIPPING}/shipments`, shipmentData);
    return res?.data;
  },


  /**
   * Get shipping rate quotes from multiple carriers
   * 
   * @param {Object} shipmentData - Shipment details for rate calculation
   * @param {string} shipmentData.origin_zip - Origin postal code
   * @param {string} shipmentData.destination_zip - Destination postal code
   * @param {number} shipmentData.weight - Package weight in kg
   * @param {number} shipmentData.length - Package length in cm
   * @param {number} shipmentData.width - Package width in cm
   * @param {number} shipmentData.height - Package height in cm
   * @param {string} [shipmentData.service_type] - Service type (standard, express, overnight)
   * @returns {Promise<Object>} Rate quotes from available carriers
   * @throws {Error} If shipment data is invalid or API request fails
   */
  async getRateQuotes(shipmentData) {
    const res = await api.get('/api/v1/operations/shipping/rate-shopping/quotes', {
      params: shipmentData
    });
    return res?.data;
  },

  /**
   * Select a specific rate from available quotes
   * 
   * @param {string} quoteId - Quote identifier
   * @param {string} rateId - Selected rate identifier
   * @returns {Promise<Object>} Confirmation of rate selection
   * @throws {Error} If quote or rate not found
   */
  async selectRate(quoteId, rateId) {
    const res = await api.post('/api/v1/operations/shipping/rate-shopping/select', {
      quote_id: quoteId,
      rate_id: rateId
    });
    return res?.data;
  },

  /**
   * Get historical rate shopping data
   * 
   * @param {Object} [filters={}] - Query filters
   * @param {string} [filters.date_from] - Start date (ISO 8601)
   * @param {string} [filters.date_to] - End date (ISO 8601)
   * @param {string} [filters.carrier_id] - Filter by carrier
   * @param {number} [filters.page] - Page number for pagination
   * @param {number} [filters.limit] - Items per page
   * @returns {Promise<Object>} Historical rate shopping records
   */
  async getRateHistory(filters = {}) {
    const res = await api.get('/api/v1/operations/shipping/rate-shopping/history', {
      params: filters
    });
    return res?.data;
  },


  /**
   * Get carrier performance metrics
   * 
   * @param {Object} [filters={}] - Query filters
   * @param {string} [filters.warehouse_id] - Filter by warehouse
   * @param {string} [filters.date_from] - Start date (ISO 8601)
   * @param {string} [filters.date_to] - End date (ISO 8601)
   * @param {string} [filters.carrier_id] - Filter by specific carrier
   * @returns {Promise<Object>} Carrier performance data including on-time delivery, damage rates, costs
   */
  async getCarrierPerformance(filters = {}) {
    const res = await api.get(`${API_ENDPOINTS.CARRIERS}/performance`, {
      params: filters
    });
    return res?.data;
  },

  /**
   * Get carrier ranking by performance
   * 
   * @param {string} warehouseId - Warehouse identifier
   * @param {string} dateFrom - Start date (ISO 8601)
   * @param {string} dateTo - End date (ISO 8601)
   * @returns {Promise<Array>} Ranked list of carriers with performance scores
   */
  async getCarrierRanking(warehouseId, dateFrom, dateTo) {
    const res = await api.get(`${API_ENDPOINTS.CARRIERS}/performance`, {
      params: {
        warehouse_id: warehouseId,
        date_from: dateFrom,
        date_to: dateTo,
        ranking: true
      }
    });
    return res?.data;
  },

  /**
   * Get specific carrier metrics over time
   * 
   * @param {string} carrierId - Carrier identifier
   * @param {string} metric - Metric type (on_time_delivery, damage_rate, cost, etc.)
   * @param {string} dateFrom - Start date (ISO 8601)
   * @param {string} dateTo - End date (ISO 8601)
   * @returns {Promise<Object>} Time-series metric data for the carrier
   */
  async getCarrierMetrics(carrierId, metric, dateFrom, dateTo) {
    const res = await api.get(`${API_ENDPOINTS.CARRIERS}/${carrierId}`, {
      params: {
        metric,
        date_from: dateFrom,
        date_to: dateTo
      }
    });
    return res?.data;
  },


  /**
   * Get list of carriers
   * 
   * @param {Object} [filters={}] - Query filters
   * @param {boolean} [filters.is_active] - Filter by active status
   * @param {string} [filters.service_type] - Filter by service type
   * @returns {Promise<Array>} List of carrier records
   */
  async getCarriers(filters = {}) {
    const res = await api.get(API_ENDPOINTS.CARRIERS, {
      params: filters
    });
    return res?.data;
  },

  /**
   * Get detailed information for a specific carrier
   * 
   * @param {string} carrierId - Carrier identifier
   * @returns {Promise<Object>} Carrier details including contact info, services, rates
   * @throws {Error} If carrier not found
   */
  async getCarrierDetails(carrierId) {
    const res = await api.get(`${API_ENDPOINTS.CARRIERS}/${carrierId}`);
    return res?.data;
  },

  /**
   * Update carrier configuration
   * 
   * @param {string} carrierId - Carrier identifier
   * @param {Object} config - Configuration updates
   * @param {string} [config.api_key] - Carrier API key
   * @param {Object} [config.service_levels] - Available service levels
   * @param {Object} [config.rate_adjustments] - Rate adjustment rules
   * @returns {Promise<Object>} Updated carrier configuration
   * @throws {Error} If carrier not found or update fails
   */
  async updateCarrierConfig(carrierId, config) {
    const res = await api.put(`${API_ENDPOINTS.CARRIERS}/${carrierId}`, config);
    return res?.data;
  },

  /**
   * Toggle carrier active status
   * 
   * @param {string} carrierId - Carrier identifier
   * @param {boolean} isActive - New active status
   * @returns {Promise<Object>} Updated carrier record
   * @throws {Error} If carrier not found
   */
  async toggleCarrierStatus(carrierId, isActive) {
    const res = await api.patch(`${API_ENDPOINTS.CARRIERS}/${carrierId}`, {
      is_active: isActive
    });
    return res?.data;
  },

  /**
   * Create a new carrier
   * 
   * @param {Object} data - Carrier data
   * @returns {Promise<Object>} Created carrier record
   */
  async createCarrier(data) {
    const res = await api.post(API_ENDPOINTS.CARRIERS, data);
    return res?.data;
  },

  /**
   * Delete a carrier
   * 
   * @param {string} carrierId - Carrier identifier
   * @returns {Promise<Object>} Confirmation of deletion
   */
  async deleteCarrier(carrierId) {
    const res = await api.delete(`${API_ENDPOINTS.CARRIERS}/${carrierId}`);
    return res?.data;
  }
};
